/**
 * Life4Earth V2 — Pre-Flight Checklist Types
 *
 * Pre-flight items represent what a project needs before it can execute:
 * volunteers, tools, funds, permits, materials, etc. They also serve as
 * a mechanism for donor matching / conditional sponsorship.
 *
 * Design principles:
 *   - Progressive & opt-in: zero overhead by default
 *   - Hybrid taxonomy: required category enum + free-text + optional tags
 *   - Dual audience: internal (Work tab) vs public (Impact tab)
 *   - mission_subkpi_key linkage for pre-flight → mission metric handoff
 */

/* ------------------------------------------------------------------ */
/*  Resource Category (Unified Taxonomy)                               */
/* ------------------------------------------------------------------ */

/**
 * Unified resource category taxonomy. Replaces the previous dual system
 * (PREFLIGHT_CATEGORIES for resource grouping + COST_CATEGORIES for
 * budget classification) with a single 12-value enum that covers both
 * infrastructure and program-oriented BOMs.
 *
 * This is the single source of truth for classifying pre-flight items.
 * Used for: checklist grouping, budget rollups, EVM variance analysis,
 * grant reporting, marketplace matching, and import templates.
 */
export const RESOURCE_CATEGORIES = [
  'materials',
  'equipment',
  'services',
  'labor',
  'transportation',
  'facilities',
  'admin_legal',
  'knowledge',
  'financial',
  'in_kind',
  'software_tech',
  'certifications',
  'other',
] as const;

export type ResourceCategory = (typeof RESOURCE_CATEGORIES)[number];

/**
 * @deprecated Use RESOURCE_CATEGORIES and ResourceCategory instead.
 * Kept for backward compatibility during migration.
 */
export const PREFLIGHT_CATEGORIES = RESOURCE_CATEGORIES;
export type PreflightCategory = ResourceCategory;

/**
 * @deprecated Use RESOURCE_CATEGORIES and ResourceCategory instead.
 * The dual-category system has been merged into a single taxonomy.
 */
export const COST_CATEGORIES = RESOURCE_CATEGORIES;
export type CostCategory = ResourceCategory;

/**
 * Map legacy category values (from old PREFLIGHT_CATEGORIES or
 * COST_CATEGORIES) to new RESOURCE_CATEGORIES values.
 * Used during data migration and API response normalization.
 */
export const LEGACY_CATEGORY_MAP: Record<string, ResourceCategory> = {
  // Old PREFLIGHT_CATEGORIES → new
  'tools_equipment': 'equipment',
  'administrative':  'admin_legal',
  'land_site':       'facilities',
  // Old COST_CATEGORIES → new
  'material':            'materials',
  'service':             'services',
  'software':            'software_tech',
  'travel':              'transportation',
  'permit':              'admin_legal',
  'overhead':            'admin_legal',
  'autonomous_partner':  'services',
};

/**
 * Normalize a category value, mapping legacy values to new taxonomy.
 * Returns the input if already valid; falls back to 'other' if unrecognized.
 */
export function normalizeCategory(raw: string | null | undefined): ResourceCategory {
  if (!raw) return 'materials';
  const valid = new Set<string>(RESOURCE_CATEGORIES);
  if (valid.has(raw)) return raw as ResourceCategory;
  return LEGACY_CATEGORY_MAP[raw] ?? 'other';
}

/* ------------------------------------------------------------------ */
/*  Resource Phase                                                     */
/* ------------------------------------------------------------------ */

/**
 * Lifecycle phase for resource items:
 *   - preparation:  needed before the project can launch
 *   - operational:  consumed during execution
 *   - sustainment:  needed for long-term maintenance post-execution
 */
export const RESOURCE_PHASES = [
  'preparation',
  'operational',
  'sustainment',
] as const;

export type ResourcePhase = (typeof RESOURCE_PHASES)[number];

/**
 * Map legacy / backend phase values to the canonical RESOURCE_PHASES.
 * The backend may return alternate names (e.g. Gantt-oriented labels).
 */
export const LEGACY_PHASE_MAP: Record<string, ResourcePhase> = {
  origination: 'preparation',
  planning:    'preparation',
  execution:   'operational',
  closeout:    'sustainment',
  maintenance: 'sustainment',
};

/**
 * Normalize a phase value, mapping legacy/backend values to the
 * canonical 3-value taxonomy. Returns 'preparation' for null/unrecognized.
 */
export function normalizePhase(raw: string | null | undefined): ResourcePhase {
  if (!raw) return 'preparation';
  const valid = new Set<string>(RESOURCE_PHASES);
  if (valid.has(raw)) return raw as ResourcePhase;
  return LEGACY_PHASE_MAP[raw.toLowerCase()] ?? 'preparation';
}

/* ------------------------------------------------------------------ */
/*  Requirement Types                                                  */
/* ------------------------------------------------------------------ */

/**
 * How the item's fulfillment is measured:
 *   - threshold:  numeric value that must be met (e.g. "5 volunteers")
 *   - binary:     yes/no completion (e.g. "permit obtained")
 *   - commitment: pledge from an external party (e.g. "donor committed $5000")
 */
export const PREFLIGHT_REQUIREMENT_TYPES = [
  'threshold',
  'binary',
  'commitment',
] as const;

export type PreflightRequirementType = (typeof PREFLIGHT_REQUIREMENT_TYPES)[number];

/* ------------------------------------------------------------------ */
/*  Item State (Lifecycle State Machine)                                */
/* ------------------------------------------------------------------ */

/**
 * Lifecycle state for pre-flight items. Replaces is_fulfilled as the
 * primary indicator. See Architecture v1.2, Section 3.2.
 *
 *   - draft:     Identified but not actively pursued. Default for new items.
 *   - seeking:   Actively needed. Visible to matching engine.
 *   - blocked:   Cannot proceed — depends on unmet prerequisite items.
 *   - partial:   Some progress (e.g., need 5 volunteers, have 2). Still seeking.
 *   - met:       Fully fulfilled. Archived from active view.
 *   - withdrawn: No longer needed. Soft-deleted from active list.
 */
export const PREFLIGHT_STATES = [
  'draft',
  'seeking',
  'blocked',
  'partial',
  'met',
  'withdrawn',
] as const;

export type PreflightState = (typeof PREFLIGHT_STATES)[number];

/** States where the item is visible to the matching engine. */
export const MATCHABLE_STATES: readonly PreflightState[] = ['seeking', 'partial'];

/**
 * Valid state transitions. Key = current state, value = states it can move to.
 * Blocked → Seeking is auto-managed (when all blockers reach Met).
 */
export const STATE_TRANSITIONS: Record<PreflightState, readonly PreflightState[]> = {
  draft:     ['seeking', 'withdrawn'],
  seeking:   ['partial', 'met', 'blocked', 'withdrawn'],
  blocked:   ['seeking'],                // auto-transition when blockers Met
  partial:   ['met', 'seeking', 'withdrawn'],
  met:       ['seeking'],                // re-opened
  withdrawn: ['draft'],                  // restored
};

/* ------------------------------------------------------------------ */
/*  Priority                                                            */
/* ------------------------------------------------------------------ */

/**
 * Priority level for readiness weighting and matching urgency.
 */
export const PREFLIGHT_PRIORITIES = [
  'critical',
  'important',
  'nice_to_have',
] as const;

export type PreflightPriority = (typeof PREFLIGHT_PRIORITIES)[number];

/* ------------------------------------------------------------------ */
/*  Descriptor Tags                                                     */
/* ------------------------------------------------------------------ */

/**
 * Suggested descriptor tags for multi-dimensional filtering.
 * The category enum is the single required bucket; descriptor_tags
 * are the optional rich labels that power matching.
 */
export const DESCRIPTOR_TAGS = [
  'People',
  'Expertise',
  'Materials',
  'Equipment',
  'Facility',
  'Funding',
  'Permission',
  'Knowledge',
  'Timing',
] as const;

export type DescriptorTag = (typeof DESCRIPTOR_TAGS)[number];

/* ------------------------------------------------------------------ */
/*  Visibility                                                         */
/* ------------------------------------------------------------------ */

/**
 * Controls where the item is shown:
 *   - internal: Work tab only (team-facing)
 *   - public:   Also shown on Impact tab (donor/community-facing)
 */
export type PreflightVisibility = 'internal' | 'public';

/* ------------------------------------------------------------------ */
/*  Pre-Flight Item                                                    */
/* ------------------------------------------------------------------ */

export interface PreflightItem {
  /** Unique identifier for this item. */
  item_guid: string;

  /** Project this item belongs to. */
  project_guid: string;

  /** Lifecycle phase this resource belongs to. */
  phase: ResourcePhase;

  /** Required top-level category. */
  category: PreflightCategory;

  /** Free-text label (e.g. "Certified arborist", "Chainsaw", "Land survey"). */
  label: string;

  /** Optional longer description. */
  description?: string;

  /** How fulfillment is measured. */
  requirement_type: PreflightRequirementType;

  /**
   * For threshold items: the target quantity needed.
   * For binary/commitment: not used (null).
   */
  target_quantity?: number | null;

  /**
   * For threshold items: unit of measure (e.g. "people", "kg", "USD").
   * For binary/commitment: not used (null).
   */
  unit?: string | null;

  /**
   * Current fulfilled quantity.
   *   - threshold: numeric progress (e.g. 3 of 5 volunteers)
   *   - binary: 0 = not done, 1 = done
   *   - commitment: amount pledged so far
   */
  current_quantity: number;

  /** Whether this item is complete / fulfilled. Derived: state === 'met'. */
  is_fulfilled: boolean;

  /**
   * Lifecycle state. Replaces is_fulfilled as the primary indicator.
   * See Architecture v1.2, Section 3.2 for state machine definition.
   *
   * DEV-963: Optional at the type level because legacy seed data,
   * pre-migration API rows, and template-derived items may not carry it.
   * Runtime code in this file (computePreflightSummary, etc.) already
   * tolerates undefined: `state === 'met' || is_fulfilled` checks both
   * fields, and switch statements have default branches. New code SHOULD
   * always populate state; the optionality is for backward compatibility
   * during the Architecture v1.2 transition.
   */
  state?: PreflightState;

  /** Priority for weighted readiness scoring and matching urgency signal.
   *  DEV-963: optional during v1.2 transition; runtime uses
   *  `PRIORITY_WEIGHTS[item.priority] || 2` so undefined falls back to 2. */
  priority?: PreflightPriority;

  /**
   * Multi-tag descriptors for rich filtering and matching.
   * Coexists with category: category is the single required bucket,
   * descriptor_tags are the optional rich labels.
   * DEV-963: optional during v1.2 transition (defaults to []).
   */
  descriptor_tags?: DescriptorTag[];

  /**
   * Owner-set conditions for registry matching.
   * e.g., { must_be_local: true, certifications_required: ["arborist"] }
   */
  matching_conditions?: Record<string, unknown> | null;

  /** Matched offer GUID when fulfilled via registry. */
  linked_offer_guid?: string | null;

  /** ISO timestamp of when the item was withdrawn (soft delete). */
  withdrawn_at?: string | null;

  /** Who sees this item. */
  visibility: PreflightVisibility;

  /**
   * Optional link to a mission sub-KPI key.
   * When set, fulfilling this pre-flight item establishes a baseline
   * for the corresponding mission metric post-launch.
   */
  mission_subkpi_key?: string | null;

  /** Optional freeform tags for search/filtering. */
  tags?: string[];

  /** Display order within the project's checklist. */
  sort_order: number;

  /** Who created this item. */
  created_by: string;

  /** ISO timestamp of creation. */
  created_at: string;

  /** ISO timestamp of last update. */
  updated_at: string;

  /**
   * If this item was copied from a template, the template's GUID.
   * Informational only — no live link. Team can modify freely.
   */
  source_template_guid?: string | null;

  /** GUIDs of items that must be fulfilled before this item can proceed. */
  depends_on?: string[];

  // ── Cost / Budget Fields (Migrations 025 + 026) ────────────

  /**
   * @deprecated Use `category` instead — the dual-category system has been
   * merged. This field is kept for API round-trip compatibility and is
   * always synced to `category` during normalization.
   * DEV-963: optional during v1.2 transition (mirrors `category`).
   */
  cost_category?: ResourceCategory;

  /** Estimated cost per unit. Null for items with no monetary cost (e.g., in-kind). */
  unit_cost?: number | null;

  /** Actual cost per unit recorded after procurement. */
  actual_cost?: number | null;

  /** ISO 4217 currency code. Defaults to project's budget_currency. */
  cost_currency?: string | null;

  /** Source, vendor, or justification for the cost estimate. */
  cost_note?: string | null;

  /** Computed: unit_cost × target_quantity (null if either is null). Read-only from API. */
  estimated_total?: number | null;

  /** Computed: actual_cost × current_quantity (null if actual_cost is null). Read-only from API. */
  actual_total?: number | null;

  // ── EVM-Lite Fields (Migration 027) ────────────────────────

  /** Optional FK linking this resource to a Gantt task for EVM time-phasing. */
  gantt_task_id?: string | null;

  /** Completion percentage (0–100). For linked items, inherited from Gantt task.
   *  DEV-963: optional during v1.2 transition (defaults to derived value from
   *  current_quantity / target_quantity when threshold-typed). */
  percent_complete?: number;

  // ── Fulfillment / Acquisition Fields (DEV-647) ────────────

  /**
   * Optional note recorded when the resource transitions to 'met'.
   * Documents where the resource is, who has it, or other context.
   * Append-only pattern — new notes are appended with newlines.
   * Interim field; migrates to the commenting system in a later phase.
   */
  fulfillment_note?: string | null;

  /**
   * Optional assignee for resource acquisition responsibility.
   * When null, responsibility defaults to the project owner.
   * No UI yet — data model prep for future acquisition delegation.
   */
  assignee_guid?: string | null;
}

/* ------------------------------------------------------------------ */
/*  Pre-Flight Template                                                */
/* ------------------------------------------------------------------ */

/**
 * A reusable template of pre-flight items that can be applied to a project.
 * Templates are community-scoped or platform-scoped. Applying a template
 * copies items into the project — there is no live binding.
 */
export interface PreflightTemplate {
  /** Unique identifier for this template. */
  template_guid: string;

  /** Human-readable name (e.g. "Tree Planting Project Template"). */
  name: string;

  /** Optional description. */
  description?: string;

  /** Scope: community-level or platform-level. */
  scope: 'community' | 'platform';

  /** If community-scoped, the community entity GUID. */
  community_guid?: string | null;

  /** Template items (no project_guid — they're copied on apply). */
  items: PreflightTemplateItem[];

  /** Who created this template. */
  created_by: string;

  /** ISO timestamp. */
  created_at: string;

  /** ISO timestamp. */
  updated_at: string;
}

/**
 * A single item within a template. Same shape as PreflightItem minus
 * the project-specific fields (project_guid, current_quantity, is_fulfilled, etc.).
 */
export interface PreflightTemplateItem {
  /** Lifecycle phase for this template item. */
  phase?: ResourcePhase;

  /** Category for this template item. */
  category: PreflightCategory;

  /** Free-text label. */
  label: string;

  /** Optional description. */
  description?: string;

  /** How fulfillment will be measured. */
  requirement_type: PreflightRequirementType;

  /** Target quantity (for threshold items). */
  target_quantity?: number | null;

  /** Unit of measure (for threshold items). */
  unit?: string | null;

  /** Default visibility. */
  visibility: PreflightVisibility;

  /** Optional mission sub-KPI linkage. */
  mission_subkpi_key?: string | null;

  /** Optional tags. */
  tags?: string[];

  /** Display order. */
  sort_order: number;

  /** GUIDs of template items that must be fulfilled before this item can proceed. */
  depends_on?: string[];
}

/* ------------------------------------------------------------------ */
/*  Summary / Aggregate                                                */
/* ------------------------------------------------------------------ */

/**
 * Quick summary of a project's pre-flight readiness.
 * Computed client-side from the items list.
 */
export interface PreflightSummary {
  total_items: number;
  fulfilled_items: number;
  /** 0–100 percentage. */
  readiness_pct: number;
  /** Counts by category. */
  by_category: Record<PreflightCategory, { total: number; fulfilled: number }>;
  /** Counts by lifecycle phase. */
  by_phase: Record<ResourcePhase, { total: number; fulfilled: number }>;
  /** Counts by state. */
  by_state: Record<PreflightState, number>;
}

/** Priority weights for weighted readiness scoring. */
const PRIORITY_WEIGHTS: Record<PreflightPriority, number> = {
  critical: 3,
  important: 2,
  nice_to_have: 1,
};

/**
 * Compute a readiness summary from a list of pre-flight items.
 * Uses weighted scoring by priority (critical=3, important=2, nice_to_have=1).
 */
export function computePreflightSummary(items: PreflightItem[]): PreflightSummary {
  const byCategory = {} as Record<PreflightCategory, { total: number; fulfilled: number }>;
  const byPhase = {} as Record<ResourcePhase, { total: number; fulfilled: number }>;
  const byState = {} as Record<PreflightState, number>;

  for (const cat of PREFLIGHT_CATEGORIES) {
    byCategory[cat] = { total: 0, fulfilled: 0 };
  }
  for (const phase of RESOURCE_PHASES) {
    byPhase[phase] = { total: 0, fulfilled: 0 };
  }
  for (const s of PREFLIGHT_STATES) {
    byState[s] = 0;
  }

  let total = 0;
  let fulfilled = 0;
  let weightedTotal = 0;
  let weightedFulfilled = 0;

  const validCategories = new Set<string>(PREFLIGHT_CATEGORIES);
  const validPhases = new Set<string>(RESOURCE_PHASES);

  for (const item of items) {
    // Exclude withdrawn items from readiness calculation
    if (item.state === 'withdrawn') {
      byState.withdrawn++;
      continue;
    }

    // Normalize unrecognized categories to 'other' instead of silently dropping
    const safeCat: PreflightCategory = validCategories.has(item.category)
      ? item.category as PreflightCategory
      : 'other';
    // Normalize unrecognized phases to 'preparation' as a safety net
    const safePhase: ResourcePhase = validPhases.has(item.phase)
      ? item.phase as ResourcePhase
      : 'preparation';

    total++;
    const isMet = item.state === 'met' || item.is_fulfilled;
    const weight = PRIORITY_WEIGHTS[item.priority] || 2;
    weightedTotal += weight;

    byCategory[safeCat].total++;
    if (isMet) byCategory[safeCat].fulfilled++;

    byPhase[safePhase].total++;
    if (isMet) byPhase[safePhase].fulfilled++;
    if (isMet) {
      fulfilled++;
      weightedFulfilled += weight;
    }

    // Count by state
    if (byState[item.state] !== undefined) {
      byState[item.state]++;
    }
  }

  return {
    total_items: total,
    fulfilled_items: fulfilled,
    readiness_pct: weightedTotal > 0 ? Math.round((weightedFulfilled / weightedTotal) * 100) : 100,
    by_category: byCategory,
    by_phase: byPhase,
    by_state: byState,
  };
}

/**
 * DEV-86: Check if an item is blocked by unmet dependencies.
 * Returns the list of blocking item GUIDs (items in depends_on that are not met).
 * Uses state === 'met' as the primary check, falls back to is_fulfilled for compat.
 * All GUID comparisons are case-insensitive per project convention.
 */
export function getBlockingItems(
  item: PreflightItem,
  allItems: PreflightItem[]
): string[] {
  if (!item.depends_on?.length) return [];
  const selfGuid = item.item_guid.toUpperCase();
  const itemMap = new Map(allItems.map(i => [i.item_guid.toUpperCase(), i]));
  return item.depends_on.filter(depGuid => {
    const key = depGuid.toUpperCase();
    // Skip self-references (should not exist, but guard against bad data)
    if (key === selfGuid) return false;
    const dep = itemMap.get(key);
    return dep && dep.state !== 'met' && !dep.is_fulfilled;
  });
}

/**
 * Determine if a state transition is valid per the state machine.
 */
export function isValidTransition(from: PreflightState, to: PreflightState): boolean {
  return (STATE_TRANSITIONS[from] as readonly string[]).includes(to);
}

/**
 * Compute the auto-managed state for an item based on its dependencies.
 * If all dependencies are met and the item is currently blocked, it should
 * auto-transition to seeking. If any dependency is unmet and the item is
 * in seeking/partial, it should auto-transition to blocked.
 */
export function computeAutoState(
  item: PreflightItem,
  allItems: PreflightItem[]
): PreflightState {
  const blockers = getBlockingItems(item, allItems);

  // If blocked but all blockers are now met → auto-transition to seeking
  if (item.state === 'blocked' && blockers.length === 0) {
    return 'seeking';
  }

  // If seeking/partial but has unmet dependencies → auto-transition to blocked
  if ((item.state === 'seeking' || item.state === 'partial') && blockers.length > 0) {
    return 'blocked';
  }

  return item.state;
}

/**
 * DEV-86: Detect circular dependencies.
 * Returns true if adding proposedDeps to itemGuid would create a cycle.
 * Also returns true if proposedDeps contains itemGuid itself (self-dependency).
 * All GUID comparisons are case-insensitive per project convention.
 */
export function hasCircularDependency(
  itemGuid: string,
  proposedDeps: string[],
  allItems: PreflightItem[]
): boolean {
  const target = itemGuid.toUpperCase();
  const depMap = new Map<string, string[]>();
  for (const item of allItems) {
    depMap.set(item.item_guid.toUpperCase(), (item.depends_on ?? []).map(g => g.toUpperCase()));
  }
  const visited = new Set<string>();
  const queue = proposedDeps.map(g => g.toUpperCase());
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current === target) return true;
    if (visited.has(current)) continue;
    visited.add(current);
    const deps = depMap.get(current);
    if (deps) queue.push(...deps);
  }
  return false;
}
